import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    TextField,
    Typography,
    CircularProgress,
    IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {Link as RouterLink} from 'react-router';
import {useSession} from '../../../../../firebase/UserProvider';
import {createIir, deleteIir, listIirs} from '../../../../../firebase/firestore/iir';

/**
 * Legacy IIR list (name + question in generic_documents).
 * New IIRs are authored under Collection → IIR.
 */
const ListOfIntelligenceInformationReports = () => {
    const {user} = useSession();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [name, setName] = useState('');
    const [question, setQuestion] = useState('');
    const [saving, setSaving] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await listIirs();
            setItems(list);
        } catch (e) {
            setError(e?.message || 'Failed to load IIRs');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const handleCreate = async () => {
        if (!name.trim() || !question.trim() || saving) return;
        setSaving(true);
        try {
            await createIir({name, question, createdBy: user?.uid || null});
            setCreateOpen(false);
            setName('');
            setQuestion('');
            await reload();
        } catch (e) {
            setError(e?.message || 'Failed to create IIR');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this IIR?')) return;
        try {
            await deleteIir(id);
            await reload();
        } catch (e) {
            setError(e?.message || 'Failed to delete IIR');
        }
    };

    return (
        <Stack p={2} spacing={2} overflow="auto">
            <Alert severity="info">
                New IIRs are created from completed Collection Tasks under{' '}
                <Button component={RouterLink} to="/collection/iir" size="small">
                    Collection → IIR
                </Button>
                . This page lists legacy lightweight IIR records only.
            </Alert>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" fontWeight={700}>Intelligence Information Reports (IIR)</Typography>
                <Button variant="contained" size="small" onClick={() => setCreateOpen(true)}>
                    New IIR
                </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary">
                Lightweight product type: each IIR has a name and the question it needs to answer.
            </Typography>

            {error && <Typography color="error" variant="body2">{error}</Typography>}
            {loading ? (
                <CircularProgress size={28}/>
            ) : items.length === 0 ? (
                <Typography color="text.secondary">No IIRs yet. Create one to get started.</Typography>
            ) : (
                <Stack spacing={1.5}>
                    {items.map((item) => (
                        <Paper key={item.id} variant="outlined" sx={{p: 2}}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Stack spacing={0.5} flex={1}>
                                    <Typography fontWeight={700}>{item.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Question: {item.question}
                                    </Typography>
                                    {item.createdAt && (
                                        <Typography variant="caption" color="text.disabled">
                                            {new Date(item.createdAt).toLocaleString()}
                                        </Typography>
                                    )}
                                </Stack>
                                <IconButton size="small" aria-label="Delete IIR" onClick={() => handleDelete(item.id)}>
                                    <DeleteOutlineIcon fontSize="small"/>
                                </IconButton>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New IIR</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Question this IIR needs to answer"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={saving || !name.trim() || !question.trim()}
                    >
                        {saving ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default ListOfIntelligenceInformationReports;
